import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import InspectionContext from '../../../contexts/InspectionContext';
import PrintModal from '../../Shared/Print/PrintModal';

export interface ISiteMapPrintProps {
  onClose: () => void;
  targetDoc?: any;
  projectLevelEsriEnabled?: boolean;
  isGISEnabled?: boolean;
}

// True when the upload (or scope) carries an ESRI overlay worth offering in print
const isEsriEligible = (
  targetDoc?: any,
  projectLevelEsriEnabled?: boolean,
  isGISEnabled?: boolean,
) => {
  if (!targetDoc) return projectLevelEsriEnabled && isGISEnabled; // project-level scope — show; backend gates per-upload
  const pu = targetDoc.ProjectUploadModel || targetDoc;
  const t = pu?.site_map_type;
  return (
    (t === 'gis_esri' || t === 'gis_image_esri') &&
    !!pu?.gis_calibrated &&
    projectLevelEsriEnabled &&
    isGISEnabled
  );
};

export const getMapOptionsSchema = (
  targetDocId: any,
  targetDoc?: any,
  projectLevelEsriEnabled?: boolean,
  isGISEnabled?: boolean
) => ({
  map_options: {
    label: 'Map Options',
    controlType: UIControlType.plainText,
    style: { marginBottom: 0 },
  },
  include_map_changelog: {
    label: 'Include Changelog',
    controlType: UIControlType.checkbox,
    style: {},
  },
  include_legend_item_tags: {
    label: 'Include Legend Item Tags',
    controlType: UIControlType.checkbox,
    style: {},
  },
  include_blank_maps: {
    label: targetDocId === 0 ? 'Include Blank Maps' : 'Include Blank Map',
    controlType: UIControlType.checkbox,
    style: {},
  },
  ...(isEsriEligible(targetDoc, projectLevelEsriEnabled, isGISEnabled) && {
    include_esri_layers: {
      label: 'Include ESRI Layers',
      controlType: UIControlType.checkbox,
      style: {},
    },
  }),
});

/**
 * @param {{[k:string]: any}} formData
 */
const getPrintSiteMapSchema = (
  targetDocId: any,
  targetDoc?: any,
  projectLevelEsriEnabled?: boolean,
  isGISEnabled?: boolean
) => {
  const schema: {
    [key: string]: any;
  } = {
    ...getMapOptionsSchema(targetDocId, targetDoc, projectLevelEsriEnabled, isGISEnabled),
  };

  return schema;
};

const SiteMapPrint: React.FC<ISiteMapPrintProps> = ({
  onClose,
  targetDoc,
  projectLevelEsriEnabled,
  isGISEnabled,
}) => {
  const context = useContext(InspectionContext);

  return (
    <PrintModal
      jobName='sitemap'
      jobData={{
        inspectionId: context.inspectionId,
        targetDocId: targetDoc ? targetDoc.id : null,
      }}
      statusUpdateKey={context.inspectionId}
      onClose={onClose}
      formSchema={getPrintSiteMapSchema(
        targetDoc ? targetDoc.id : 0,
        targetDoc,
        projectLevelEsriEnabled,
        isGISEnabled
      )}
      initialFormData={{
        include_map_changelog: false,
        include_esri_layers: false,
      }}
      modalTitle='Print Site Map'
      validateData={null}
    />
  );
};

SiteMapPrint.propTypes = {
  onClose: PropTypes.func.isRequired,
  targetDoc: PropTypes.object,
};

export default SiteMapPrint;
